import { useMemo, useState } from "react";
import type { GeneratedStatus, HandOnRequest, InstanceDetail, NodeAssignmentView } from "@keydials/api-client";
import { useWorkspaceServices } from "../context";
import { Sheet } from "../components/Sheet";
import { allocationLabel, type NodeScope } from "./allocation";
import { distinctInstanceMembers, memberLabel } from "./userAttribute";
import { responsibleAt, responsibleContextOf } from "./responsibleAttribute";

/**
 * Allocation (KEY-916), on a node's card: the badge saying who has it ("Allocated to Sam · due 4 Nov") with
 * Reassign / Withdraw / Re-send, and the Allocate sheet that hands it out — to a member, or to one of the dial's
 * contacts, who is invited as a guest over just this node. The same sheet answers "Hand on to…" when a status that
 * hands the work on is picked. The server is the authority on every rule; this says them up front.
 */

/** What the host lets this viewer do here. */
export interface AllocationAccess {
  /** Editor or Admin at the node — the bar for handing it out. */
  canAllocate: (scope: NodeScope) => boolean;
  /** A dial admin: may reassign or withdraw once somebody has it. */
  isDialAdmin: boolean;
}

/** What opening the Allocate sheet needs: the node, and — for a hand-on — what to do with the person chosen. */
export interface AllocateOpen {
  scope: NodeScope;
  nodeName: string;
  statuses?: GeneratedStatus[];
  mode: "allocate" | "handOn";
  handOnStatusName?: string;
  onHandOn?: (handOn: HandOnRequest) => void;
}

/** Threaded down to the item rows: present only when allocation is on for this dial. */
export interface AllocationUi {
  instanceId: string;
  access?: AllocationAccess;
  onRefresh?: () => void;
  open: (request: AllocateOpen) => void;
}

const CONTACT_ROLES = ["Editor", "Reviewer", "Reader"] as const;

const todayPlus = (days: number) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const pill = {
  display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, fontWeight: 600,
  padding: "3px 10px", borderRadius: 99, background: "var(--accent-soft)", color: "var(--text)",
  border: "var(--border-w) solid var(--accent-border)",
} as const;
const linkButton = {
  border: "none", background: "none", padding: 0, cursor: "pointer", color: "var(--accent)", fontSize: 12, fontWeight: 600,
} as const;

/** The badge, plus its actions. Renders nothing when the node isn't allocated. */
export function AllocationBadge({
  instanceId, assignment, access, onReassign, onChanged,
}: {
  instanceId: string;
  assignment: NodeAssignmentView;
  access?: AllocationAccess;
  onReassign: () => void;
  onChanged?: () => void;
}) {
  const services = useWorkspaceServices();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function run(action: "withdraw" | "resend") {
    const token = services.getAccessToken();
    if (!token) return;
    setBusy(true);
    setNote(null);
    try {
      if (action === "withdraw") {
        await services.api.withdrawAllocation(token, instanceId, assignment.id);
        onChanged?.();
      } else {
        const invite = await services.api.resendAllocation(token, instanceId, assignment.id);
        setNote(invite.outcome === "Sent" ? "Invite sent again." : "Couldn't send it — check the contact's email.");
      }
    } catch (e) {
      setNote(e instanceof Error ? e.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={pill} title={assignment.assignedByName ? `Allocated by ${assignment.assignedByName}` : undefined}>
      {allocationLabel(assignment)}
      {access?.isDialAdmin && (
        <>
          <button type="button" style={linkButton} disabled={busy} onClick={onReassign}>Reassign</button>
          <button type="button" style={linkButton} disabled={busy} onClick={() => void run("withdraw")}>Withdraw</button>
        </>
      )}
      {assignment.assigneeKind === "Contact" && access?.canAllocate(assignment) && (
        <button type="button" style={linkButton} disabled={busy} onClick={() => void run("resend")}>Re-send invite</button>
      )}
      {note && <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>{note}</span>}
    </span>
  );
}

/** The "Allocate" button a node shows when nobody has it and the viewer may hand it out. */
export function AllocateButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="kd-btn" style={{ fontSize: 12.5, padding: "3px 10px" }} onClick={onClick}>
      Allocate
    </button>
  );
}

type Choice = { kind: "Member" | "Contact"; id: string };

/**
 * The sheet that hands a node out. In "allocate" mode it saves the allocation itself; in "handOn" mode it hands the
 * chosen person back to the caller, which sends it with the status change that hands the work on.
 */
export function AllocateSheet({
  instanceId, detail, scope, nodeName, statuses, mode, handOnStatusName, onClose, onAllocated, onHandOn,
}: {
  instanceId: string;
  detail: InstanceDetail;
  scope: NodeScope;
  /** "Supplier A › Documents › Insurance" — what is being handed out. */
  nodeName: string;
  /** The statuses that may be set at the same time (allocate mode); the hand-on ones are left out. */
  statuses?: GeneratedStatus[];
  mode: "allocate" | "handOn";
  handOnStatusName?: string;
  onClose: () => void;
  onAllocated?: () => void;
  onHandOn?: (handOn: HandOnRequest) => void;
}) {
  const services = useWorkspaceServices();
  const members = useMemo(() => distinctInstanceMembers([...(detail.members ?? [])]).filter((m) => !m.archived), [detail.members]);
  const contacts = useMemo(() => [...(detail.contacts ?? [])].sort((a, b) => a.order - b.order), [detail.contacts]);
  // The Responsible person (KEY-917) is offered first, where the dial names one here.
  const responsible = useMemo(
    () => responsibleAt(responsibleContextOf(detail), { sectionKey: scope.sectionKey, segmentKey: scope.segmentKey ?? "", itemKey: scope.itemKey ?? undefined }),
    [detail, scope.sectionKey, scope.segmentKey, scope.itemKey],
  );
  const [choice, setChoice] = useState<Choice | null>(() => (responsible ? { kind: responsible.kind, id: responsible.id } : null));
  const [contactRole, setContactRole] = useState<string>("Editor");
  const [due, setDue] = useState(() => todayPlus(7));
  const [statusKey, setStatusKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isResponsible = (kind: Choice["kind"], id: string) =>
    !!responsible && responsible.kind === kind && responsible.id === id.toLowerCase();
  const value = choice ? `${choice.kind}:${choice.id}` : "";
  const ready = !!choice && /^\d{4}-\d{2}-\d{2}$/.test(due);

  async function save() {
    if (!choice || !ready) return;
    const request: HandOnRequest = {
      assigneeKind: choice.kind, assigneeId: choice.id, dueDate: due,
      contactRole: choice.kind === "Contact" ? contactRole : null,
    };
    if (mode === "handOn") {
      onHandOn?.(request);
      return;
    }
    const token = services.getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await services.api.allocateNode(token, instanceId, {
        ...request,
        sectionKey: scope.sectionKey,
        segmentKey: scope.segmentKey ?? null,
        itemKey: scope.itemKey ?? null,
        statusKey: statusKey || null,
      });
      onAllocated?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't allocate that.");
    } finally {
      setBusy(false);
    }
  }

  const label = { display: "flex", flexDirection: "column", gap: 5, fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" } as const;
  const field = {
    padding: "8px 10px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)",
    background: "var(--surface-2)", color: "var(--text)", font: "inherit", fontSize: 14,
  } as const;

  return (
    <Sheet
      title={mode === "handOn" ? `Hand on: ${handOnStatusName ?? "next step"}` : "Allocate"}
      subtitle={nodeName}
      onClose={onClose}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="kd-focusable kd-btn-quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="kd-focusable kd-btn-primary" disabled={!ready || busy} onClick={() => void save()}>
            {mode === "handOn" ? "Hand on" : "Allocate"}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <label style={label}>
          {mode === "handOn" ? "Who it goes to next" : "Who"}
          <select
            aria-label="Allocate to"
            style={field}
            value={value}
            onChange={(e) => {
              const [kind, id] = e.target.value.split(":");
              setChoice(kind && id ? { kind: kind as Choice["kind"], id } : null);
            }}
          >
            <option value="">Choose someone…</option>
            {members.length > 0 && (
              <optgroup label="Members">
                {members.map((m) => (
                  <option key={m.userId} value={`Member:${m.userId}`}>
                    {memberLabel(m)}{isResponsible("Member", m.userId) ? " (responsible)" : ""}
                  </option>
                ))}
              </optgroup>
            )}
            {contacts.length > 0 && (
              <optgroup label="Contacts">
                {contacts.map((c) => (
                  <option key={c.id} value={`Contact:${c.id}`}>
                    {c.name}{c.email ? "" : " (no email)"}{isResponsible("Contact", c.id) ? " (responsible)" : ""}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {choice?.kind === "Contact" && (
            <span style={{ fontWeight: 400, color: "var(--text-faint)" }}>
              We&rsquo;ll email them a link to just this part of the dial — they don&rsquo;t need an account.
            </span>
          )}
        </label>

        {choice?.kind === "Contact" && (
          <label style={label}>
            What they can do there
            <select aria-label="Contact access" style={field} value={contactRole} onChange={(e) => setContactRole(e.target.value)}>
              {CONTACT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}

        <label style={label}>
          Due
          <input type="date" aria-label="Due date" style={field} value={due} onChange={(e) => setDue(e.target.value)} required />
          {scope.segmentKey && (
            <span style={{ fontWeight: 400, color: "var(--text-faint)" }}>Becomes its review date, so the usual reminders chase it.</span>
          )}
        </label>

        {mode === "allocate" && statuses && statuses.length > 0 && (
          <label style={label}>
            Set its status too (optional)
            <select aria-label="Status on allocating" style={field} value={statusKey} onChange={(e) => setStatusKey(e.target.value)}>
              <option value="">Leave as it is</option>
              {statuses.filter((s) => !s.handsOn).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </label>
        )}

        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
          While it&rsquo;s theirs, only they or a dial admin can change its status. It ends when they mark it complete.
        </p>
        {error && <p style={{ margin: 0, fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}
      </div>
    </Sheet>
  );
}
